/** @jsx jsx */
import { Box, Button, Container, Flex, Heading, PencilIcon } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { initialWorkstyles } from "@/data/consultant-profiles";
import { WorkStyleType } from "@/data/consultant-profiles/types/WorkStyleType";
import { mapWorkStyleTypeToIcon } from "@/data/consultant-profiles/utils/mapWorkStyleTypeToIcon";
import { maxJobApplicationStepKey } from "@/data/job-applications/utils/maxJobApplicationStepKey";
import { formatMultipleLocations } from "@/data/locations";
import { SingleWorkStyle } from "@/views/consultant-profiles/details/work-styles/SingleWorkStyle";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import {
  ApplyWorkstylesForm,
  transformDraftWithWorkstyles,
  WorkStylesFormValues,
  workStylesValidation,
} from "@/views/job-applications/apply/_workstyles/ApplyWorkStylesForm";

import { ApplyWizardBackButton } from "../_components/ApplyWizardBackButton";

interface ApplyWizardWorkstylesProps {}

export const ApplyWizardWorkstyles: FC<ApplyWizardWorkstylesProps> = ({}) => {
  const { t } = useTranslation("session");
  const { sendEvent } = useAnalytics();

  const isInitialRender = useRef(true);

  const { jobRequest, goToNextStep, draft, setDraft, profile, createOrUpdateDraft } = useApplyWizard();

  const anyProfileWorkstyles = Boolean(profile?.workStyles?.length);
  const anyDraftWorkstyles = Boolean(draft?.workStyles?.length);
  const sortedDraftWorkStyles = draft?.workStyles?.sort((a, b) => (a.type > b.type ? 1 : -1));
  const sortedProfileWorkStyles = profile?.workStyles?.sort((a, b) => (a.type > b.type ? 1 : -1));
  const workStylesChanged = JSON.stringify(sortedDraftWorkStyles) !== JSON.stringify(sortedProfileWorkStyles);

  const [editMode, setEditMode] = useState(
    !anyProfileWorkstyles && !anyDraftWorkstyles
      ? true
      : anyProfileWorkstyles && !anyDraftWorkstyles
      ? false
      : workStylesChanged,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const FORM_INITIAL_VALUES = {
    workStyles: draft?.workStyles?.length
      ? draft.workStyles
      : profile?.workStyles?.length
      ? profile.workStyles
      : initialWorkstyles,
  };

  const handleSubmit = useCallback(
    async (values?: WorkStylesFormValues) => {
      setIsSubmitting(true);

      try {
        await createOrUpdateDraft(draft => ({
          ...transformDraftWithWorkstyles(values, profile!)(draft),
          step: maxJobApplicationStepKey(draft?.step, "WORKSTYLES"),
        }));

        sendEvent({
          type: "apply_job_work_styles_complete",
          id: jobRequest.id,
          name: jobRequest.title!,
          location: formatMultipleLocations(t, jobRequest.locations, jobRequest.locations?.length),
        });
      } catch (error) {
        // NOTE: errors are ignored on purpose
      } finally {
        setIsSubmitting(false);
        goToNextStep();
      }
    },
    [createOrUpdateDraft, goToNextStep, profile, sendEvent, jobRequest, t],
  );

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleContinueButtonClick = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    if (isInitialRender.current) {
      setDraft(d => ({ ...d!, step: maxJobApplicationStepKey(draft?.step, "QUESTIONNAIRE") }));
      isInitialRender.current = false;
    }
  });

  return (
    <Box py="md">
      <Container variant="narrow">
        <Flex px="sm" flexDirection="column" gap="md">
          <ApplyWizardBackButton />

          <Flex flexGrow={1} flexDirection="column" gap="md">
            <Flex justifyContent="space-between" alignItems="center">
              <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.WORKSTYLES")}</Heading>
              {!editMode && (
                <Button display="inline-block" iconLeft={<PencilIcon />} size="sm" variant="ghost" onClick={handleEdit}>
                  {t("JOB_REQUESTS.APPLY.WIZARD.EDIT")}
                </Button>
              )}
            </Flex>
          </Flex>
          {editMode ? (
            <Formik
              initialValues={{
                ...FORM_INITIAL_VALUES,
              }}
              validationSchema={workStylesValidation}
              onSubmit={handleSubmit}>
              {(formikProps: FormikProps<WorkStylesFormValues>) => <ApplyWorkstylesForm form={formikProps} />}
            </Formik>
          ) : (
            <React.Fragment>
              <Flex flexDirection="column" gap="sm">
                {FORM_INITIAL_VALUES.workStyles
                  ?.sort((a, b) => b.score - a.score)
                  .map(workStyle => (
                    <SingleWorkStyle
                      key={workStyle.type}
                      WorkStyleIcon={mapWorkStyleTypeToIcon[workStyle.type as keyof Record<WorkStyleType, React.FC>]}
                      {...workStyle}
                    />
                  ))}
              </Flex>

              <Flex>
                <Button onClick={handleContinueButtonClick} disabled={isSubmitting} size="lg">
                  {t("JOB_REQUESTS.APPLY.WIZARD.CONTINUE")}
                </Button>
              </Flex>
            </React.Fragment>
          )}
        </Flex>
      </Container>
    </Box>
  );
};
